import { LINE_HEIGHT_PX } from '../page-template';
import { getCompanyLetterhead } from '../company-letterhead';
import { estimateWrappedLineCount } from '../description-chunks';
import type { PrintDocumentModel, PrintRow, PrintSection, QuotationPrintModel } from '../types';
import { formatMoney, PRINT_PLAIN_AMOUNT_PROFILE } from '@/utils/format';
import { formatQuotationPrintIdentity } from '@/utils/quotationRevisionPolicy';
import {
  buildQuotationNoteChunks,
  buildQuotationTermsAndConditionsChunks,
  QUOTATION_NOTES_CHARS_PER_LINE,
  quotationPrintShowsItemCode,
} from '../quotation-print-model';
import { estimateDensePrintLines, richTextToPlain } from '@/utils/richText';

function formatAmount(value: unknown): string {
  return formatMoney(value, PRINT_PLAIN_AMOUNT_PROFILE);
}

function textH(text: string, cpl: number, lineHeightPx = LINE_HEIGHT_PX): number {
  return Math.max(lineHeightPx, estimateDensePrintLines(text, cpl) * lineHeightPx);
}

/** Notes use the full letter content width, same measure as terms and conditions. */
const NOTES_CPL = QUOTATION_NOTES_CHARS_PER_LINE;
/** Full-width T&C body (~740px ≈ 110 chars at 12px UI font). */
const TERMS_AND_CONDITIONS_CPL = 110;
/** Match .qtn-extra-terms 11px × 1.35 line-height more closely than table LINE_HEIGHT. */
const TERMS_AND_CONDITIONS_LINE_PX = 15;
/** `.qtn-rich-text p { margin-bottom: 0.45em }` at 11px. */
const RICH_BLOCK_GAP_PX = 5;
/** `.qtn-notes-compact`: 11px × 1.25 line-height, 0.15em paragraph gap. */
const NOTES_LINE_PX = 14;
const NOTES_BLOCK_GAP_PX = 2;

/** Rich-text chunk height: wrapped lines per block plus the paragraph gap between blocks. */
function richTextH(
  chunk: string,
  cpl: number,
  lineHeightPx = TERMS_AND_CONDITIONS_LINE_PX,
  blockGapPx = RICH_BLOCK_GAP_PX
): number {
  const blocks = richTextToPlain(chunk)
    .split('\n')
    .map((b) => b.trim())
    .filter(Boolean);
  if (!blocks.length) return lineHeightPx;
  const lines = blocks.reduce((sum, b) => sum + Math.max(1, estimateDensePrintLines(b, cpl)), 0);
  return lines * lineHeightPx + (blocks.length - 1) * blockGapPx;
}

function itemColumns(model: QuotationPrintModel): Array<{ key: string; label: string }> {
  const code = quotationPrintShowsItemCode(model) ? [{ key: 'itemCode', label: 'Item Code' }] : [];
  const priceCols = model.technicalMode
    ? []
    : [
        { key: 'unitPrice', label: `Unit Price ${model.totals.currency}` },
        { key: 'net', label: `Total ${model.totals.currency}` },
      ];
  const deliveryCol = model.technicalMode ? [{ key: 'delivery', label: 'Delivery' }] : [];
  if (model.manufacturingColumns) {
    return [
      { key: 'sno', label: 'S.No' },
      ...code,
      { key: 'revision', label: 'Rev' },
      { key: 'description', label: 'Description' },
      { key: 'qty', label: 'Quantity' },
      ...deliveryCol,
      ...priceCols,
    ];
  }
  return [
    { key: 'sno', label: 'S.No' },
    ...code,
    { key: 'description', label: 'Description' },
    { key: 'revision', label: 'Rev' },
    { key: 'qty', label: 'Quantity' },
    ...deliveryCol,
    ...priceCols,
  ];
}

/** Map rich quotation model → generic PrintDocumentModel. */
export function quotationToPrintDocument(model: QuotationPrintModel): PrintDocumentModel {
  const printIdentity = formatQuotationPrintIdentity(model.quotationNumber, model.revisionNumber);
  const customerLines =
    2 +
    (model.customerAddress ? estimateWrappedLineCount(model.customerAddress, 45) : 0) +
    (model.projectName ? 1 : 0) +
    1;
  const refLines =
    4 +
    (model.inquiryReference ? 1 : 0) +
    (model.sapNumber ? 1 : 0) +
    (model.opportunityNo ? 1 : 0) +
    (model.estimationNo ? 1 : 0) +
    (model.manufacturer ? 1 : 0);

  const rows: PrintRow[] = model.items.map((item) => {
    const cells: Record<string, string> = {
      itemCode: item.itemCode || '—',
      revision: item.revision || '—',
      qty: item.quantityLabel,
      unitPrice: formatAmount(item.unitPrice),
      net: formatAmount(item.netAmount),
      delivery: item.deliveryLabel || '—',
    };
    if (item.kind === 'rental') {
      cells.uom = item.uom || '';
      cells.period = item.period || '';
      cells.duration = item.durationLabel || '';
      if (!model.rentalCompactCustomerPrint) {
        cells.dates = item.datesText || '';
      }
      cells.rate = formatAmount(item.rate);
    }
    const extraLines = [
      item.specifications,
      item.notes,
      item.rentalCharges,
      ...(item.rentalBreakdownLines || []),
    ].filter(Boolean) as string[];

    return {
      id: item.id,
      lineNumber: item.lineNumber,
      description: item.description,
      descriptionChunks: item.descriptionChunks.length
        ? item.descriptionChunks
        : [item.description || ''],
      extraLines,
      cells,
      kind: item.kind,
    };
  });

  let overheadH = 0;
  if (model.customerOverheadSummary) overheadH += 24;
  if (model.overheadLines.length) overheadH += 20 + model.overheadLines.length * LINE_HEIGHT_PX;

  const totalsRows = 2 + (model.totals.discount > 0 ? 2 : 0) + 1;
  const totalsH = totalsRows * 20 + 8;
  const wordsH = textH(model.totals.amountInWords, 70) + 18;
  // Compact Delivery/Payment table only — long notes paginate as separate chunks.
  const termsTableH = 24 + model.terms.length * LINE_HEIGHT_PX + 8;
  const noteChunks =
    model.noteChunks?.length > 0
      ? model.noteChunks
      : buildQuotationNoteChunks(model.notes, model.specialInstructions);
  const termsAndConditionsChunks = model.technicalMode
    ? []
    : model.termsAndConditionsChunks?.length > 0
      ? model.termsAndConditionsChunks
      : buildQuotationTermsAndConditionsChunks(model.extraTerms);
  const sigH = model.signers.length > 0 ? 36 + model.signers.length * 72 : 0;

  const finalSections: PrintSection[] = [];
  if (!model.technicalMode) {
    if (overheadH > 0) finalSections.push({ id: 'overhead', heightPx: overheadH, keepTogether: true });
    finalSections.push({
      id: 'totals',
      heightPx: totalsH,
      keepTogether: true,
      keepTogetherWithNext: true,
    });
    finalSections.push({ id: 'amountWords', heightPx: wordsH, keepTogether: true });
  }
  if (model.terms.length > 0) {
    finalSections.push({ id: 'terms', heightPx: termsTableH, keepTogether: true });
  }
  noteChunks.forEach((chunk, idx) => {
    const headingPad = idx === 0 ? 4 : 22;
    finalSections.push({
      id: `notesChunk:${idx}`,
      heightPx: headingPad + richTextH(chunk, NOTES_CPL, NOTES_LINE_PX, NOTES_BLOCK_GAP_PX) + 8,
      keepTogether: true,
    });
  });
  termsAndConditionsChunks.forEach((chunk, idx) => {
    const headingPad = idx === 0 ? 26 : 4;
    finalSections.push({
      id: `tcChunk:${idx}`,
      // Clause-sized units pack into whatever height is left under the notes.
      heightPx: headingPad + richTextH(chunk, TERMS_AND_CONDITIONS_CPL) + 6,
      keepTogether: true,
    });
  });
  if (sigH > 0) finalSections.push({ id: 'signatures', heightPx: sigH, keepTogether: true });

  return {
    docType: 'quotation',
    title: model.title,
    subtitle: model.subtitle,
    documentNumber: model.quotationNumber,
    partyName: model.customerName,
    templateId: 'letter-letterhead',
    letterhead: getCompanyLetterhead(),
    continuationLabel: `Quotation ${printIdentity} | ${model.customerName}`,
    firstPageSections: [
      {
        id: 'documentHeader',
        heightPx: 28 + (model.subtitle ? 18 : 0) + 8,
      },
      {
        id: 'customerBlock',
        heightPx: Math.max(customerLines, refLines) * LINE_HEIGHT_PX + 12,
      },
      {
        id: 'intro',
        heightPx: textH(`${model.introGreeting}\n${model.introText}`, 90) + 16,
      },
    ],
    tables: [
      {
        id: 'items',
        columns: itemColumns(model),
        rows,
      },
    ],
    finalSections,
    renderPayload: { ...model, noteChunks, termsAndConditionsChunks },
  };
}
